import { z } from "zod";

const emailSchema = z
  .string()
  .trim()
  .min(1, "Email is required")
  .max(254, "Email is too long")
  .email("Invalid email address")
  .transform((value) => value.toLowerCase());

const passwordSchema = z
  .string()
  .min(10, "Password must be at least 10 characters")
  .max(72, "Password must not exceed 72 characters");

export const registerSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  fullName: z
    .string()
    .trim()
    .min(1, "Full name is required")
    .max(120, "Full name is too long"),
});

export const forgotPasswordSchema = z.object({ email: emailSchema });

/** Redeeming a reset link. The token is opaque; only its length is bounded. */
export const resetPasswordSchema = z.object({
  token: z.string().trim().min(16, "Invalid reset link").max(128, "Invalid reset link"),
  password: passwordSchema,
});

/**
 * Changing a password from inside the app. Gated on the current password, so a
 * borrowed unlocked laptop cannot silently take the account over.
 */
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required").max(72),
  newPassword: passwordSchema,
});

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required").max(72),
});

/**
 * A second factor: either a 6-digit TOTP code, or one of the backup codes
 * (10 hex chars, optionally dash-grouped). Kept as one loose field so the user
 * can paste either into the same box without choosing a mode first; the server
 * decides which it is. Length is bounded to keep the bcrypt backup-code compare
 * from being handed anything large.
 */
export const mfaVerifySchema = z.object({
  code: z.string().trim().min(6, "Enter your code").max(24, "That code is too long"),
});

/** Confirming enrollment requires a live TOTP code — backup codes don't exist yet. */
export const mfaConfirmSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, "Enter the 6-digit code from your authenticator app"),
});

/** Turning MFA off is a downgrade, so it is password-gated. */
export const mfaDisableSchema = z.object({
  password: z.string().min(1, "Password is required").max(72),
});

export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;
export type RegisterInput = z.infer<typeof registerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type MfaVerifyInput = z.infer<typeof mfaVerifySchema>;
export type MfaConfirmInput = z.infer<typeof mfaConfirmSchema>;
export type MfaDisableInput = z.infer<typeof mfaDisableSchema>;
